<template>
	<div
		class="g-block-pay-audio-player"
		:class="{
			'g-block-pay-audio-player--edit': editable,
			'is-active': editable && active,
			'g-block-pay-audio-player--h5': h5,
			'g-block-pay-audio-player--unlocked': isUnlocked,
		}"
		:style="{ width: blockStyle.playerWidth }"
		@click="onWrapClick"
	>
		<div class="g-block-pay-audio-player__card">
			<div
				class="g-block-pay-audio-player__shell"
				:style="shellStyle"
			>
				<img
					v-if="bgUrl"
					class="g-block-pay-audio-player__bg"
					:src="bgUrl"
					alt=""
				/>
				<div
					v-if="blockStyle.darkOverlay"
					class="g-block-pay-audio-player__overlay"
				></div>

				<div class="g-block-pay-audio-player__content">
					<div class="g-block-pay-audio-player__progress-wrap">
						<div
							ref="progressTrack"
							class="g-block-pay-audio-player__progress-track"
							:class="{ 'is-interactive': isInteractive }"
							:style="trackStyle"
							@click="onSeek"
							@mousedown.stop="onProgressMouseDown"
							@touchstart.stop.prevent="onProgressTouchStart"
						>
							<div
								class="g-block-pay-audio-player__progress-bar"
								:style="progressBarStyle"
							></div>
							<div
								class="g-block-pay-audio-player__progress-thumb"
								:style="thumbStyle"
							></div>
						</div>
						<div class="g-block-pay-audio-player__time" :style="timeStyle">
							<span>{{ currentTimeText }}</span>
							<span>{{ durationText }}</span>
						</div>
					</div>

					<div class="g-block-pay-audio-player__controls">
						<button
							type="button"
							class="g-block-pay-audio-player__play"
							:style="iconStyle"
							:aria-label="isPlaying ? '暂停' : '播放'"
							@click.stop="togglePlay"
						>
							<span v-if="isPlaying" class="g-block-pay-audio-player__pause"></span>
							<span v-else class="g-block-pay-audio-player__triangle"></span>
						</button>
					</div>
				</div>
			</div>

			<p class="g-block-pay-audio-player__title">{{ playerData.title || '助眠音乐' }}</p>

			<PayMediaPaySection
				class="g-block-pay-audio-player__pay"
				:currency="playerData.currency"
				:price="playerData.price"
				:button-text="playerData.buttonText"
				:button-bg="playerData.buttonBg"
				:show-info-bar="showPayInfoBar"
				:info-text="previewInfoText"
				:is-unlocked="isUnlocked"
				:h5="h5"
				@unlock="onUnlockClick"
			/>
		</div>

		<audio
			v-if="audioUrl"
			ref="audioEl"
			:src="audioUrl"
			preload="metadata"
			@loadedmetadata="onLoadedMetadata"
			@timeupdate="onTimeUpdate"
			@ended="onEnded"
			@pause="onPause"
			@play="onPlay"
		></audio>
	</div>
</template>

<script>
	import baseFunc from '../../../../config/base_func.js';
	import { geditPayUnlockMixin } from '../../../mixins/geditPayUnlock.js';
	import PayMediaPaySection from '../../../components/PayMediaPaySection.vue';
	import { formatAudioDuration } from '../../../utils/localAudio.js';
	import {
		getAudioPlayerAudioUrl,
		getAudioPlayerCoverUrl,
		getAudioPlayerBlockStyle,
	} from '../../../utils/audioPlayer.js';
	import {
		formatPayPreviewDuration,
	} from '../../../utils/payMediaCommon.js';
	import {
		getPayAudioPlayerPreviewHint,
		isPayAudioPlayerUnlocked,
		normalizePayAudioPlayerData,
		setPayAudioPlayerUnlocked,
	} from '../../../utils/payAudioPlayer.js';

	export default {
		name: 'GPayAudioPlayerShell',
		mixins: [geditPayUnlockMixin],
		components: { PayMediaPaySection },
		props: {
			data: {
				type: Object,
				required: true,
			},
			blockId: {
				type: String,
				default: '',
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
			compact: {
				type: Boolean,
				default: false,
			},
			idKey: {
				type: String,
				default: '',
			},
		},
		emits: ['select'],
		data() {
			return {
				unlockedLocal: false,
				isPlaying: false,
				currentTime: 0,
				duration: 0,
				permissionedAudioUrl: '',
				mediaPlayLimit: null,
				hasRequestedPermission: false,
				isDragging: false,
				draggingTime: 0,
			};
		},
		computed: {
			playerData() {
				return normalizePayAudioPlayerData(this.data);
			},
			blockStyle() {
				return getAudioPlayerBlockStyle(this.playerData, { h5: this.h5 });
			},
			bgUrl() {
				return getAudioPlayerCoverUrl(this.playerData);
			},
			audioUrl() {
				return this.permissionedAudioUrl || getAudioPlayerAudioUrl(this.playerData);
			},
			shellStyle() {
				return {
					borderRadius: this.blockStyle.borderRadius,
				};
			},
			trackStyle() {
				return {
					background: this.hexToRgba(this.blockStyle.themeColor, 0.35),
				};
			},
			progressBarStyle() {
				return {
					width: `${this.progressPercent}%`,
					background: this.blockStyle.themeColor,
				};
			},
			thumbStyle() {
				return {
					left: `${this.progressPercent}%`,
					background: this.blockStyle.themeColor,
				};
			},
			timeStyle() {
				return { color: this.blockStyle.themeColor };
			},
			iconStyle() {
				return { color: this.blockStyle.themeColor };
			},
			progressPercent() {
				const total = this.duration;
				if (!total) return 0;
				if (this.isDragging) {
					return Math.min(100, (this.draggingTime / total) * 100);
				}
				return Math.min(100, (this.currentTime / total) * 100);
			},
			displayProgressPercent() {
				return this.progressPercent;
			},
			currentTimeText() {
				if (this.isDragging) return formatAudioDuration(this.draggingTime);
				return formatAudioDuration(this.currentTime);
			},
			/** 右侧总时长始终为完整音频时长（试听时长仅展示在试听提示条） */
			durationText() {
				if (this.duration) {
					return this.playerData.audio?.durationText || formatAudioDuration(this.duration);
				}
				return this.playerData.audio?.durationText || '00:00';
			},
			isUnlocked() {
				if (this.editable) return false;
				return this.unlockedLocal || isPayAudioPlayerUnlocked(this.blockId) || this.isGeditModulePaid;
			},
			isInteractive() {
				return this.editable || this.h5;
			},
			shouldLimitPlayback() {
				return !this.isUnlocked && this.h5 && !this.editable;
			},
			previewLimit() {
				return this.playerData.freePreviewDuration;
			},
			showPayInfoBar() {
				if (this.isUnlocked) return false;
				const total = this.playerData.audio?.duration || 0;
				return this.previewLimit > 0 && total > 0 && this.previewLimit < total;
			},
			previewInfoText() {
				const duration = formatPayPreviewDuration(this.previewLimit);
				const hint = getPayAudioPlayerPreviewHint(this.playerData);
				return hint.replace(duration, `<strong>${duration}</strong>`);
			},
		},
		watch: {
			blockId: {
				immediate: true,
				handler() {
					this.syncUnlockState();
				},
			},
			data: {
				handler() {
					this.resetPlayer();
				},
				deep: true,
			},
		},
		mounted() {
			this.duration = this.playerData.audio?.duration || 0;
		},
		beforeUnmount() {
			this.pauseAudio();
			document.removeEventListener('mousemove', this._onGlobalMouseMove);
			document.removeEventListener('mouseup', this._onGlobalMouseUp);
			document.removeEventListener('touchmove', this._onGlobalTouchMove);
			document.removeEventListener('touchend', this._onGlobalTouchEnd);
		},
		methods: {
			syncUnlockState() {
				this.unlockedLocal = isPayAudioPlayerUnlocked(this.blockId);
			},
			hexToRgba(hex, alpha) {
				const normalized = String(hex || '#e84c3d').replace('#', '');
				const full = normalized.length === 3
					? normalized.split('').map(c => c + c).join('')
					: normalized;
				const num = parseInt(full, 16);
				if (Number.isNaN(num)) return `rgba(232, 76, 61, ${alpha})`;
				const r = (num >> 16) & 255;
				const g = (num >> 8) & 255;
				const b = num & 255;
				return `rgba(${r}, ${g}, ${b}, ${alpha})`;
			},
		onWrapClick() {
			if (this.editable) {
				this.$emit('select');
			}
		},
			onUnlockClick() {
				this.runGeditUnlockFlow({
					applyUnlock: () => {
						setPayAudioPlayerUnlocked(this.blockId, true);
						this.unlockedLocal = true;
					},
				});
			},
			resetPlayer() {
				this.pauseAudio();
				this.currentTime = 0;
				this.duration = this.playerData.audio?.duration || 0;
			},
			onLoadedMetadata() {
				const audio = this.$refs.audioEl;
				if (audio && Number.isFinite(audio.duration)) {
					this.duration = Math.floor(audio.duration);
				}
			},
			onTimeUpdate() {
				const audio = this.$refs.audioEl;
				if (!audio) return;
				if (this.shouldLimitPlayback && audio.currentTime > this.previewLimit) {
					audio.pause();
					audio.currentTime = this.previewLimit;
				}
				this.currentTime = audio.currentTime || 0;
			},
			onPlay() {
				this.isPlaying = true;
			},
			onPause() {
				this.isPlaying = false;
			},
			onEnded() {
				this.isPlaying = false;
				this.currentTime = 0;
				const audio = this.$refs.audioEl;
				if (audio) {
					audio.currentTime = 0;
				}
			},
			togglePlay() {
				if (!this.isInteractive) return;
				const audio = this.$refs.audioEl;
				if (!audio) return;
				if (this.isPlaying) {
					audio.pause();
				} else {
					if (this.h5 && this.idKey && this.playerData.audio?.id && !this.hasRequestedPermission) {
						this.hasRequestedPermission = true;
						this.requestPlayPermission().then(() => {
							audio.play();
						}).catch(err => {
							this.$message.error(err.data?.message || '请求失败');
							audio.pause();
							this.hasRequestedPermission = false;
						});
					} else {
						audio.play();
					}
				}
			},
			//访问路径中取指定的值
			getQueryStringByChannel(name) {
				const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')
				if (!window.location.href.split('?')[1]) {
					return null
				}
				const re = window.location.href.split('?')[1].match(reg)
				if (re != null) return unescape(re[2])
				return null
			},
			requestPlayPermission() {
				return this.$apiRequest.post('/api/getMediaPlayPermissions', {
					attach_id: this.playerData.audio.id,
					id_key: this.idKey,
					unique: '',
					app_type:1,
					p_key: this.getQueryStringByChannel('p_key')==null?'':this.getQueryStringByChannel('p_key'),
				}).then(res => {
					if (res.data?.play_url) {
						this.permissionedAudioUrl = baseFunc.getQiniusign(res.data.play_url);
					}
					if (res.data?.media_play_limit !== undefined) {
						this.mediaPlayLimit = res.data.media_play_limit;
					}
				})
			},
			pauseAudio() {
				const audio = this.$refs.audioEl;
				if (audio) {
					audio.pause();
				}
				this.isPlaying = false;
			},
			onSeek(e) {
				if (!this.isInteractive || !this.duration) return;
				const track = this.$refs.progressTrack;
				const audio = this.$refs.audioEl;
				if (!track || !audio) return;
				const rect = track.getBoundingClientRect();
				const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
				let targetTime = ratio * this.duration;
				if (this.shouldLimitPlayback) {
					targetTime = Math.min(targetTime, this.previewLimit);
				}
				audio.currentTime = targetTime;
				this.currentTime = audio.currentTime;
			},
			onProgressMouseDown(event) {
				this._startDrag(event.clientX);
				document.addEventListener('mousemove', this._onGlobalMouseMove);
				document.addEventListener('mouseup', this._onGlobalMouseUp);
			},
			onProgressTouchStart(event) {
				const touch = event.touches?.[0];
				if (!touch) return;
				this._startDrag(touch.clientX);
				document.addEventListener('touchmove', this._onGlobalTouchMove, { passive: false });
				document.addEventListener('touchend', this._onGlobalTouchEnd);
			},
			_startDrag(clientX) {
				if (!this.isInteractive || !this.duration) return;
				this.isDragging = true;
				this.draggingTime = this.currentTime;
				this._updateDrag(clientX);
			},
			_onGlobalMouseMove(event) {
				this._updateDrag(event.clientX);
			},
			_onGlobalMouseUp() {
				this._endDrag();
				document.removeEventListener('mousemove', this._onGlobalMouseMove);
				document.removeEventListener('mouseup', this._onGlobalMouseUp);
			},
			_onGlobalTouchMove(event) {
				event.preventDefault();
				const touch = event.touches?.[0];
				if (!touch) return;
				this._updateDrag(touch.clientX);
			},
			_onGlobalTouchEnd() {
				this._endDrag();
				document.removeEventListener('touchmove', this._onGlobalTouchMove);
				document.removeEventListener('touchend', this._onGlobalTouchEnd);
			},
			_updateDrag(clientX) {
				if (!this.isDragging || !this.duration) return;
				const track = this.$refs.progressTrack;
				if (!track) return;
				const rect = track.getBoundingClientRect();
				if (!rect.width) return;
				const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
				let targetTime = ratio * this.duration;
				if (this.shouldLimitPlayback) {
					targetTime = Math.min(targetTime, this.previewLimit);
				}
				this.draggingTime = targetTime;
			},
			_endDrag() {
				if (!this.isDragging) return;
				const targetTime = this.draggingTime;
				this.isDragging = false;
				this.currentTime = targetTime;
				const audio = this.$refs.audioEl;
				if (audio) {
					audio.currentTime = targetTime;
				}
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-pay-audio-player {
		box-sizing: border-box;
		max-width: 100%;
		margin: 0 auto;

		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-pay-audio-player__card {
		background: #fff;
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
	}

	.g-block-pay-audio-player__title {
		margin: 0;
		padding: 12px 14px;
		font-size: 15px;
		font-weight: 600;
		line-height: 1.4;
		color: #1f1f1f;
	}

	.g-block-pay-audio-player__shell {
		position: relative;
		width: 100%;
		aspect-ratio: 2.35 / 1;
		overflow: hidden;
		background: #1a1a2e;
	}

	.g-block-pay-audio-player__bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.g-block-pay-audio-player__overlay {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.35);
		pointer-events: none;
	}

	.g-block-pay-audio-player__content {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		height: 100%;
		padding: 14px 16px 12px;
		box-sizing: border-box;
	}

	.g-block-pay-audio-player__progress-wrap {
		margin-top: auto;
		padding-top: 8px;
	}

	.g-block-pay-audio-player__progress-track {
		position: relative;
		height: 3px;
		border-radius: 2px;
		overflow: visible;

		&.is-interactive {
			cursor: pointer;
		}
	}

	.g-block-pay-audio-player__progress-bar {
		position: absolute;
		left: 0;
		top: 0;
		height: 100%;
		border-radius: inherit;
		pointer-events: none;
	}

	.g-block-pay-audio-player__progress-thumb {
		position: absolute;
		top: 50%;
		width: 10px;
		height: 10px;
		margin: -5px 0 0 -5px;
		border-radius: 50%;
		pointer-events: none;
		opacity: 0;
		transition: opacity 0.15s;

		.is-interactive & {
			opacity: 1;
		}
	}

	.g-block-pay-audio-player__time {
		display: flex;
		justify-content: space-between;
		margin-top: 6px;
		font-size: 11px;
		line-height: 1;
	}

	.g-block-pay-audio-player__controls {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 28px;
		margin-top: 4px;
	}

	.g-block-pay-audio-player__play {
		border: none;
		background: transparent;
		padding: 0;
		cursor: default;
		line-height: 1;
		outline: none;

		.g-block-pay-audio-player--edit & {
			cursor: pointer;
		}
	}

	.g-block-pay-audio-player__triangle {
		display: block;
		width: 0;
		height: 0;
		margin-left: 4px;
		border-top: 10px solid transparent;
		border-bottom: 10px solid transparent;
		border-left: 16px solid currentColor;
	}

	.g-block-pay-audio-player__pause {
		display: block;
		width: 14px;
		height: 16px;
		border-left: 4px solid currentColor;
		border-right: 4px solid currentColor;
		box-sizing: border-box;
	}

	.g-block-pay-audio-player--h5 {
		.g-block-pay-audio-player__card {
			border-radius: 0.24rem;
			box-shadow: 0 0.04rem 0.24rem rgba(0, 0, 0, 0.2);
		}

		.g-block-pay-audio-player__content {
			padding: 0.28rem 0.32rem 0.24rem;
		}

		.g-block-pay-audio-player__progress-wrap {
			padding-top: 0.16rem;
		}

		.g-block-pay-audio-player__progress-track {
			height: 0.06rem;
			border-radius: 0.04rem;
		}

		.g-block-pay-audio-player__progress-thumb {
			width: 0.2rem;
			height: 0.2rem;
			margin: -0.1rem 0 0 -0.1rem;
			opacity: 1;
		}

		.g-block-pay-audio-player__time {
			margin-top: 0.12rem;
			font-size: 0.22rem;
		}

		.g-block-pay-audio-player__controls {
			min-height: 0.56rem;
			margin-top: 0.08rem;
		}

		.g-block-pay-audio-player__play {
			cursor: pointer;
		}

		.g-block-pay-audio-player__triangle {
			margin-left: 0.08rem;
			border-top-width: 0.2rem;
			border-bottom-width: 0.2rem;
			border-left-width: 0.32rem;
		}

		.g-block-pay-audio-player__pause {
			width: 0.28rem;
			height: 0.32rem;
			border-left-width: 0.08rem;
			border-right-width: 0.08rem;
		}

		.g-block-pay-audio-player__pay {
			:deep(.g-pay-media-pay) {
				padding: 0.28rem;
				border-radius: 0.24rem;
			}

			:deep(.g-pay-media-pay__info) {
				gap: 0.16rem;
				padding: 0.2rem 0.24rem;
				border-radius: 0.2rem;
				font-size: 0.24rem;
			}

			:deep(.g-pay-media-pay__main) {
				margin-top: 0.24rem;
			}

			:deep(.g-pay-media-pay__info-icon) {
				font-size: 0.28rem;
			}

			:deep(.g-pay-media-pay__currency) {
				font-size: 0.28rem;
			}

			:deep(.g-pay-media-pay__price-value) {
				font-size: 0.56rem;
			}

			:deep(.g-pay-media-pay__unlock-btn) {
				padding: 0.2rem 0.32rem;
				font-size: 0.26rem;
			}
		}

		.g-block-pay-audio-player__title {
			padding: 0.28rem 0.32rem;
			font-size: 0.3rem;
		}
	}
</style>
